import { useApiBase } from './useApiBase'

/**
 * Shared game data API (#684). Session-scoped, exactly like cloud save (#450):
 * the backend resolves the scope (app identity) from the session and the player
 * from the JWT. Anonymous players do not take part (auth required).
 * See doc/architecture/cloud_save.md.
 */
export interface SharedFile {
  filename: string
  /** base64-encoded bytes */
  content: string
  sizeBytes: number
}

export interface ListSharedResponse {
  scope: string | null
  files: SharedFile[]
}

export interface PutSharedResponse {
  filename: string
  score: number
  best: number
  /** Files to inject back into the frame (the freshly aggregated ranking). */
  files: SharedFile[]
}

export function useSharedDataApi() {
  const { apiBase, authenticatedFetch } = useApiBase()

  /** Files the frame should hold for this session's scope (v1: ranking.json). */
  const listShared = (sessionId: string) =>
    authenticatedFetch<ListSharedResponse>(
      `${apiBase}/api/v1/sessions/${encodeURIComponent(sessionId)}/shared`,
      { method: 'GET' },
    )

  /** Submit one shared file (v1: "score.json"); the response carries the new ranking. */
  const putShared = (sessionId: string, filename: string, contentBase64: string) =>
    authenticatedFetch<PutSharedResponse>(
      `${apiBase}/api/v1/sessions/${encodeURIComponent(sessionId)}/shared`,
      { method: 'PUT', body: JSON.stringify({ filename, content: contentBase64 }) },
    )

  return { listShared, putShared }
}
